import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';

/**
 * Small 24-hour trend sparkline. Purely presentational: samples come from
 * the parent, which shares a single batched fetch across all rows.
 */
const AlertTrendSparkline = ({
  samples,
  isLoading,
  width,
  height,
  strokeColor,
  thresholdValue,
  thresholdColor,
  isBoolean,
}) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (isLoading || !samples || samples.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const padding = 2;
    const xScale = d3.scaleTime()
      .domain(d3.extent(samples, (d) => d.time))
      .range([padding, width - padding]);

    // Normalize sample accessor so booleans/0-1 numbers both work
    // eslint-disable-next-line no-nested-ternary
    const getValue = (d) => (isBoolean ? (d.value ? 1 : 0) : d.value);

    let yScale;
    if (isBoolean) {
      // Fixed 0/1 domain with a little inner padding so the top/bottom lines are visible
      yScale = d3.scaleLinear()
        .domain([0, 1])
        .range([height - padding - 1, padding + 1]);
    } else {
      const values = samples.map((d) => d.value);
      const yMin = Math.min(...values, thresholdValue != null ? thresholdValue : Infinity);
      const yMax = Math.max(...values, thresholdValue != null ? thresholdValue : -Infinity);
      yScale = d3.scaleLinear()
        .domain([yMin, yMax])
        .nice()
        .range([height - padding, padding]);
    }

    // Threshold line only makes sense for numeric channels
    if (!isBoolean && thresholdValue != null) {
      svg.append('line')
        .attr('x1', padding)
        .attr('x2', width - padding)
        .attr('y1', yScale(thresholdValue))
        .attr('y2', yScale(thresholdValue))
        .attr('stroke', thresholdColor)
        .attr('stroke-dasharray', '2,2')
        .attr('stroke-width', 1);
    }

    const line = d3.line()
      .x((d) => xScale(d.time))
      .y((d) => yScale(getValue(d)))
      .curve(isBoolean ? d3.curveStepAfter : d3.curveMonotoneX);

    svg.append('path')
      .datum(samples)
      .attr('fill', 'none')
      .attr('stroke', strokeColor)
      .attr('stroke-width', 1.5)
      .attr('d', line);
  }, [isLoading, samples, width, height, strokeColor, thresholdValue, thresholdColor, isBoolean]);

  if (isLoading) {
    return (
      <div
        className="bg-light rounded"
        style={{ width, height }}
        aria-label="Loading trend"
      />
    );
  }

  if (!samples || samples.length === 0) {
    return (
      <div
        className="text-muted small d-flex align-items-center justify-content-center"
        style={{ width, height }}
      >
        No Data
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      role="img"
      aria-label="24 hour trend"
    />
  );
};

AlertTrendSparkline.propTypes = {
  samples: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.instanceOf(Date),
    value: PropTypes.number,
  })),
  isLoading: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  strokeColor: PropTypes.string,
  thresholdValue: PropTypes.number,
  thresholdColor: PropTypes.string,
  isBoolean: PropTypes.bool,
};

AlertTrendSparkline.defaultProps = {
  samples: null,
  isLoading: false,
  width: 120,
  height: 30,
  strokeColor: '#28a745',
  thresholdValue: null,
  thresholdColor: '#000000',
  isBoolean: false,
};

export default AlertTrendSparkline;
